// Section 13 case screen for one open case ("ask each case"): the crime, the offender's history
// (memory lines), hunger state and family, their chance of crime with its explain() tooltip,
// and the per-case override with every response (locked ones disabled, with the reason in words).
// Props only; the Justice panel (or an overlay, M6) passes the case and the answer action.
import type { JusticeResponse, PromptId } from '../../../sim/api';
import type { PendingCaseVM } from '../../../viewModels/justice';
import { humanize, percent } from '../../format';
import { seasonLabel, justiceText as t } from '../../text/en';
import { Num } from '../../tooltip/num';
import './justice.css';

interface Props {
  vm: PendingCaseVM;
  /** Answers are allowed only while deciding (24.7). */
  disabled: boolean;
  onAnswer: (promptId: PromptId, response: JusticeResponse) => void;
}

function OffenderFacts({ vm }: { vm: NonNullable<PendingCaseVM['details']> }) {
  return (
    <dl class="justice-facts">
      <dt>{t.facts.age}</dt>
      <dd class="num">{vm.ageYears}</dd>
      <dt>{t.facts.job}</dt>
      <dd>{vm.jobLabel}</dd>
      <dt>{t.facts.trust}</dt>
      <dd>
        <Num value={vm.trust} format="whole" />
      </dd>
      <dt>{t.facts.happiness}</dt>
      <dd>
        <Num value={vm.happiness} format="whole" />
      </dd>
      <dt>{t.facts.health}</dt>
      <dd>
        <Num value={vm.health} format="whole" />
      </dd>
      <dt>{t.facts.strength}</dt>
      <dd>
        <Num value={vm.strength} format="whole" />
      </dd>
      <dt>{t.facts.hunger}</dt>
      <dd>{vm.hungry ? t.fedShort(percent(vm.fed), vm.hungrySeasons) : t.fedFull}</dd>
      <dt>{t.facts.offences}</dt>
      <dd class="num">{vm.offences}</dd>
      <dt>{t.facts.crimeChance}</dt>
      <dd>
        <Num
          value={vm.crimeRisk.value}
          format="percent"
          factors={vm.crimeRisk.factors}
          label={t.facts.crimeChance}
        />
      </dd>
    </dl>
  );
}

function History({ vm }: { vm: NonNullable<PendingCaseVM['details']> }) {
  return (
    <>
      <h5>{t.family}</h5>
      {vm.family.length === 0 ? (
        <p class="justice-muted">{t.noFamily}</p>
      ) : (
        <ul class="justice-list">
          {vm.family.map((f) => (
            <li key={f.id}>
              <span class="justice-name">{f.name}</span>, {t.relations[f.relation]}
              {!f.present && <span class="justice-muted"> ({t.away})</span>}
            </li>
          ))}
        </ul>
      )}
      <h5>{t.history}</h5>
      {vm.memory.length === 0 ? (
        <p class="justice-muted">{t.noHistory}</p>
      ) : (
        <ul class="justice-list">
          {vm.memory.map((m, i) => (
            <li key={`${m.turn}-${i}`}>
              <span class="justice-muted">{t.when(seasonLabel[m.season], m.year)}:</span>{' '}
              {t.memory[m.kind] ?? humanize(m.kind)}
              {m.otherName !== null && <> ({m.otherName})</>}
            </li>
          ))}
        </ul>
      )}
      {vm.priorCases.length > 0 && (
        <>
          <h5>{t.priorCases}</h5>
          <ul class="justice-list">
            {vm.priorCases.map((c) => (
              <li key={c.id}>
                {t.crimes[c.crime]}, {t.when(seasonLabel[c.stamp.season], c.stamp.year)}:{' '}
                {c.response === null ? t.notJudged : t.responses[c.response]}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

export function JusticePrompt({ vm, disabled, onAnswer }: Props) {
  const name = `justice-${vm.id}`;
  const headingId = `${name}-title`;
  return (
    <article class="justice-case" aria-labelledby={headingId}>
      <h4 id={headingId}>
        {t.caseTitle(t.crimes[vm.crime], vm.offender.name)}{' '}
        <span class="justice-muted">({t.when(seasonLabel[vm.stamp.season], vm.stamp.year)})</span>
      </h4>
      {vm.victims.length > 0 && (
        <p>
          {t.columns.victims}: {vm.victims.map((v) => v.name).join(', ')}
        </p>
      )}
      {vm.details === null ? (
        <p class="justice-note">{t.offenderGone}</p>
      ) : (
        <div class="justice-case__body">
          <OffenderFacts vm={vm.details} />
          <div>
            <History vm={vm.details} />
          </div>
        </div>
      )}
      <fieldset class="justice-choice" disabled={disabled}>
        <legend>{t.judgement}</legend>
        {vm.options.map((o) => {
          const id = `${name}-${o.response}`;
          const hintId = `${id}-hint`;
          return (
            <div key={o.response} class={`justice-option${o.available ? '' : ' is-locked'}`}>
              <input
                type="radio"
                id={id}
                name={name}
                value={o.response}
                checked={vm.chosen === o.response}
                disabled={!o.available}
                aria-describedby={hintId}
                onChange={() => onAnswer(vm.promptId, o.response)}
              />
              <label for={id}>
                {t.responses[o.response]}
                {o.response === vm.proposed && <span class="justice-tag"> ({t.proposed})</span>}
              </label>
              <span id={hintId} class="justice-muted justice-option__hint">
                {o.block === null ? t.responseHints[o.response] : t.blocks[o.block]}
              </span>
            </div>
          );
        })}
      </fieldset>
    </article>
  );
}
